import { ArrowBack, ArrowForward } from '@mui/icons-material';
import React, { useEffect, useRef } from 'react';

import CHorizontalScrollContainer from '../../components/CHorizontalScrollContainer';
import XDButton from '../../components/buttons/XDButton';
import { modulo, updateStateAttribute, useStateExtended } from '../../helpers/helper';
import { useAppSelector } from '../../hooks/hooks';

interface Props {
  id: string;
  children: React.ReactNode;
  /**
   * Interval in seconds
   */
  interval?: number;
  navigationStyle?: 'button' | 'card' | 'none';
  cardSpacerStyle?: Pick<React.CSSProperties, 'gap'>;
  propagateEdgeClicks?: boolean;
  activeIndex?: number;
}

interface State {
  scrollerRef?: HTMLElement;
  parentScrollLeft: number;
  paddingWidth: number;
  cardBounds: Array<{ left: number; right: number }>;
  cardStates: {
    spotlightIndex: number;
    prevIndex: number;
    nextIndex: number;
    fullyVisible: number[];
  };
}

/**
 * @deprecated Use CMagneticSnapCarousel instead.
 * @param props
 * @constructor
 */
export default function CSnappingCarousel(props: Props) {
  const { dashboard } = useAppSelector((state) => state);
  const parentRef = useRef<HTMLDivElement>(null);
  const [state, setState, getState] = useStateExtended<State>({
    parentScrollLeft: 0,
    paddingWidth: 0,
    cardBounds: [],
    cardStates: {
      spotlightIndex: -1,
      prevIndex: -1,
      nextIndex: -1,
      fullyVisible: []
    }
  });

  const prevHandler = async () => {
    const state = await getState();
    state.scrollerRef?.scrollTo({
      left: (state.cardBounds?.[state.cardStates.prevIndex].left ?? 0) - (state.cardBounds?.[0].left ?? 0),
      behavior: 'smooth'
    });
  };

  const nextHandler = async () => {
    const state = await getState();
    state.scrollerRef?.scrollTo({
      left: (state.cardBounds?.[state.cardStates.nextIndex].left ?? 0) - (state.cardBounds?.[0].left ?? 0),
      behavior: 'smooth'
    });
  };

  const handleScrollTo = (ev: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    state.scrollerRef?.scrollTo({
      left:
        ((ev.target as HTMLDivElement).getBoundingClientRect().left ?? 0) -
        (parentRef.current?.getBoundingClientRect().left ?? 0),
      behavior: 'smooth'
    });
  };

  useEffect(() => {
    if (props.activeIndex === undefined) return;

    (async () => {
      const currentState = await getState();

      const targetCard = currentState.cardBounds[props.activeIndex ?? 0];
      const firstCard = currentState.cardBounds[0];

      if (!currentState.scrollerRef || !targetCard || !firstCard) return;

      currentState.scrollerRef.scrollTo({
        left: targetCard.left - firstCard.left,
        behavior: 'smooth'
      });
    })();
  }, [props.activeIndex, state.cardBounds.length]);

  useEffect(() => {
    const scrollerDOM = document.querySelector(`#${props.id}`)?.parentElement?.parentElement?.parentElement;
    updateStateAttribute<State>({ scrollerRef: scrollerDOM ?? undefined }, setState);

    const scrollHandler = async () => {
      const computeClosestIndex = async () => {
        const deltaFromScrollProgress = (await getState()).cardBounds.map((e) =>
          Math.abs(e.left - dashboard.dashboardOutletDimensions.columnLeft)
        );
        return deltaFromScrollProgress?.findIndex((e) => e === Math.min(...(deltaFromScrollProgress ?? []))) ?? 0;
      };
      const computeFullyVisibleCards = async () => {
        const errorMargin = 5; // We need error margin because the state is computed shortly before last position is reached!
        const boolArray = (await getState()).cardBounds.map((e, ix) => {
          const leftEdgeInPort = e.left >= dashboard.dashboardOutletDimensions.columnLeft - errorMargin;
          const rightEdgeInPort =
            e.right <=
            dashboard.dashboardOutletDimensions.columnLeft + dashboard.dashboardOutletDimensions.columnW + errorMargin;
          return leftEdgeInPort && rightEdgeInPort ? ix : -1;
        });
        return boolArray.filter((e) => e > -1);
      };
      updateStateAttribute<State>(
        {
          parentScrollLeft: scrollerDOM?.scrollLeft,
          cardStates: {
            spotlightIndex: await computeClosestIndex(),
            prevIndex: modulo((await computeClosestIndex()) - 1, React.Children.count(props.children)),
            nextIndex: modulo((await computeClosestIndex()) + 1, React.Children.count(props.children)),
            fullyVisible: await computeFullyVisibleCards()
          },
          cardBounds: Array.from(document.querySelector(`#${props.id}`)?.children ?? []).map((e) => {
            return {
              left: e.getBoundingClientRect().left,
              right: e.getBoundingClientRect().right
            };
          })
        },
        setState
      );
    };

    const resizeHandler = () => {
      updateStateAttribute<State>(
        {
          cardBounds: Array.from(document.querySelector(`#${props.id}`)?.children ?? []).map((e) => {
            return { left: e.getBoundingClientRect().left, right: e.getBoundingClientRect().right };
          })
        },
        setState
      );
    };

    scrollerDOM?.addEventListener('scroll', scrollHandler);
    window.addEventListener('resize', resizeHandler);

    (async () => {
      resizeHandler();
      await scrollHandler();
    })();

    return () => {
      scrollerDOM?.removeEventListener('scroll', scrollHandler);
      window.removeEventListener('resize', resizeHandler);
    };
  }, [props.children]);

  useEffect(() => {
    if (!props.interval) return;
    const autoPlay = setInterval(nextHandler, props.interval * 1000);
    return () => {
      clearInterval(autoPlay);
    };
  }, [state.cardStates.nextIndex]);

  return (
    <>
      {React.Children.count(props.children) > 0 && (
        <div className={'flex flex-col gap-4'}>
          {/* Content */}
          <CHorizontalScrollContainer hideHelper hideScroller>
            <div
              id={props.id}
              className={'flex gap-2 sm:gap-8 items-stretch'}
              ref={parentRef}
              style={props.cardSpacerStyle}
            >
              {React.Children.map(props.children, (e, ix) => (
                <div
                  key={ix}
                  className={'relative'}
                  style={{
                    scrollSnapAlign: 'start',
                    scrollMarginLeft:
                      ix + 1 !== React.Children.count(props.children) ? dashboard.dashboardOutletDimensions.padding : 0
                  }}
                  onClick={(ev) => {
                    if (props.propagateEdgeClicks) {
                      handleScrollTo(ev);
                    }
                  }}
                >
                  {e}
                  {!state.cardStates.fullyVisible.includes(ix) && !props.propagateEdgeClicks && (
                    <div className={'cursor-pointer absolute top-0 left-0 w-full h-full'} onClick={handleScrollTo} />
                  )}
                </div>
              ))}
            </div>

            {/* Pseudo navigation elements */}
            <div className={'h-0 overflow-hidden'}>
              <span id={`${props.id}-prev`} onClick={prevHandler} />
              <span id={`${props.id}-next`} onClick={nextHandler} />
            </div>
          </CHorizontalScrollContainer>

          {/* Navigation */}
          {state.cardStates.fullyVisible.length !== state.cardBounds.length && props.navigationStyle !== 'none' && (
            <>
              {props.navigationStyle === 'card' ? (
                <div className={'flex gap-4 w-full justify-center'}>
                  {state.cardBounds?.map((e, ix) => {
                    const isActive = state.cardStates.spotlightIndex === ix;
                    return (
                      <div
                        key={ix}
                        onClick={() => {
                          state.scrollerRef?.scrollTo({
                            left: e.left - (state.cardBounds?.[0]?.left ?? 0),
                            behavior: 'smooth'
                          });
                        }}
                        className={
                          'cursor-pointer rounded-3xl h-2 bg-slate-300 overflow-hidden relative transition-[width] ease-in-out duration-300'
                        }
                        style={{
                          width: isActive ? '1.5rem' : '0.5rem',
                          backgroundColor: isActive ? 'rgb(148, 163, 184)' : 'rgb(203, 213, 225)'
                        }}
                      >
                        <div
                          className={'w-0 rounded-3xl bg-slate-700 h-2 transition-[width]'}
                          style={{
                            width: isActive ? '100%' : '0px',
                            opacity: isActive ? 1 : 0,
                            transitionDuration: isActive ? (props.interval ?? 0) + 's' : '0s',
                            transitionTimingFunction: 'linear'
                          }}
                        />
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div
                  className={'flex gap-2 sm:gap-4 justify-end w-full'}
                  style={{
                    paddingRight: dashboard.dashboardOutletDimensions.padding,
                    paddingLeft: dashboard.dashboardOutletDimensions.padding
                  }}
                >
                  <XDButton
                    onClick={prevHandler}
                    className={'aspect-square bg-gray-100'}
                    style={{ padding: '1rem' }}
                    disabled={state.cardStates.spotlightIndex === 0}
                  >
                    <ArrowBack style={{ fontSize: '125%' }} />
                  </XDButton>
                  <XDButton
                    onClick={nextHandler}
                    className={'aspect-square bg-gray-100'}
                    style={{ padding: '1rem' }}
                    disabled={state.cardStates.fullyVisible.includes(React.Children.count(props.children) - 1)}
                  >
                    <ArrowForward style={{ fontSize: '125%' }} />
                  </XDButton>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </>
  );
}
